/**
 * Pageviews for the self-hosted Umami, when the server put its tracker in the page (only prod
 * does; see voice/static.mjs). The tracker's own auto-tracking hears pushState and nothing else,
 * and every route here is a hash change, so the router reports each one itself.
 */

interface Umami {
  track(payload: (props: Record<string, unknown>) => Record<string, unknown>): unknown;
  track(event: string, data?: Record<string, string | number | boolean>): unknown;
}

/** `#/academy/lesson-3?seed=1` → `/academy/lesson-3`. Query and deep-link parameters stay out. */
export function pathFromHash(hash: string): string {
  const path = (hash.replace(/^#/, '').split('?')[0] ?? '').replace(/\/+$/, '');
  return path.startsWith('/') ? path : `/${path}`;
}

let last = '';

export function trackPageview(hash: string = location.hash): void {
  const url = pathFromHash(hash);
  // The operator console is not player traffic.
  if (url === last || url.startsWith('/admin')) return;
  const umami = (window as { umami?: Umami }).umami;
  if (!umami) return;
  last = url;
  try { umami.track((props) => ({ ...props, url, title: document.title })); } catch {}
}

/**
 * The funnel events gtm/README.md asks for. `once` names a scope (a game's id, say) inside which
 * the event is reported a single time, so a refresh or a second landing does not inflate it.
 * No-ops wherever the tracker is absent: dev, local builds and blocked scripts.
 */
export type FunnelEvent = 'game_start' | 'first_clearance' | 'first_landing' | 'lesson_start' | 'lesson_complete' | 'shift_complete' | 'debrief_view' | 'retry' | 'idle_pause' | 'landing_cta' | 'mobile_notice';
const sent = new Set<string>();
export function trackEvent(name: FunnelEvent, data: Record<string, string | number | boolean> = {}, once?: string): void {
  if (once) { const key = `${name}:${once}`; if (sent.has(key)) return; sent.add(key); }
  const umami = (window as { umami?: Umami }).umami;
  if (!umami) return;
  try { umami.track(name, data); } catch {}
}

const ACTIVATED_KEY = 'atc.activated.v1';
/** True the first time this browser ever issues an accepted clearance: the activation moment. */
export function firstActivation(storage: Pick<Storage, 'getItem' | 'setItem'> | undefined): boolean {
  try {
    if (storage?.getItem(ACTIVATED_KEY)) return false;
    storage?.setItem(ACTIVATED_KEY, new Date().toISOString());
    return true;
  } catch { return false; }
}
